/* global __DEV__: false */
import React from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import type { Moment } from 'moment';
// eslint-disable-next-line no-restricted-imports
import { padding, fontWeight } from '@grnhse/seedling/lib/azalea/constants';
import {
  getTimeElapsedFromStartOfDay,
  formatToLocalizedDay,
  formatToLocalizedDate,
  formatToLocalizedTime,
  areDatesOnSameDay,
} from './dateUtils';

import Dropdown from 'shared/components/select_components/native_styled_dropdown';

type DateTime = {
  date: Moment;
  time: number; // the time elapsed from midnight in milliseconds,
  momentDate: Moment;
};

export type TimeSlotValue = {
  id?: number;
  start: Moment;
  end: Moment;
};

type Props = {
  min: Moment;
  max: Moment;
  step: number;
  onChange: (arg1: TimeSlotValue) => unknown;
  disabled: boolean;
  value: TimeSlotValue;
};

type State = {
  start: DateTime;
  end: DateTime;
};

// used to speed up the rendering by trying to reduce the bottleneck of time localization thru
// moment by caching results
const cachedLocalizedStrings: Record<string, string> = {};

const ReadOnlyDateTime = styled.div`
  display: inline-block;

  & > span {
    display: block;
    ${fontWeight('heavy')};
  }

  span + span {
    margin-top: ${padding.tiny};
  }
`;

const FlexContainer = styled.div`
  display: flex;

  // try to not allow highlighting of the element when tapping
  -webkit-tap-highlight-color: transparent;
`;

const DropdownWrapper = styled.div`
  ${(props) =>
    // eslint-disable-next-line max-len
    // @ts-expect-error - TS2339 - Property 'date' does not exist on type 'ThemedStyledProps<Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "key" | keyof HTMLAttributes<HTMLDivElement>> & { ...; }, any>'.
    props.date &&
    `
    // let the date field grow to take up the remaining space
    flex: 1 1 auto;
  `}

  ${(props) =>
    // eslint-disable-next-line max-len
    // @ts-expect-error - TS2339 - Property 'time' does not exist on type 'ThemedStyledProps<Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "key" | keyof HTMLAttributes<HTMLDivElement>> & { ...; } & object, any>'.
    props.time &&
    `
    flex: 30%;
  `}
`;

type WrappedDropdownProps = {
  onChange: (arg1: React.SyntheticEvent<HTMLSelectElement>) => unknown;
  // either a number in ms representing the milliseconds elapsed since midnight
  // or a ISO8601 string for representing the date
  // either way, when set to the HTML select element it will get cast to a string
  value: string | number;
  children: ReactNode;
  date?: boolean;
  time?: boolean;
  'data-provides'?: string;
};

export const WrappedDropdown = ({
  onChange,
  value,
  children,
  date,
  time,
  'data-provides': dataProvides,
}: WrappedDropdownProps) => {
  return (
    // @ts-expect-error - TS2769 - No overload matches this call.
    <DropdownWrapper date={date} time={time}>
      <Dropdown data-provides={dataProvides} onChange={onChange} value={value}>
        {children}
      </Dropdown>
    </DropdownWrapper>
  );
};

/**
 * A component that only uses native inputs for maximum compatibility and usability on all
 * platforms especially mobile.
 *
 * It's primary job is to allow the user to pick a time slot
 *
 * IMPORTANT: For simplicity's sake, if the start date and end date are different (due to the
 *            time slots working out to something like 11pm-1am from time zone differences),
 *            it will NOT render a separate "end date" field.
 *
 *            This has a few implications. If the start date field is ever changed, it WILL
 *            change the end date day to match the start date day. See {#onStartDateChange} for
 *            details.
 *
 * @see updateDateTimeState
 * @see onStartDateChange
 * */
export default class TimeSlotInput extends React.PureComponent<Props, State> {
  static defaultProps = {
    min: moment(),
    max: moment().add(3, 'months'),
    step: 1000 * 60 * 15, // 15 minutes
    value: {
      start: moment(),
      end: moment(),
    },
    disabled: false,
    onChange: () => {
      /* do nothing */
    },
  };

  UNSAFE_componentWillMount() {
    this.updateDateTimeState(this.props);
  }

  /**
   * The internal state of the date + time is kept in a different way than one might expect.
   *
   * The `start` and `end` moment objects in `value` are each broken down into
   * the 'date' and 'time' components so they can be individually set to the dropdowns.
   *
   * - The 'date' is the same as the start value but set to the beginning of the day
   * - The 'time' is the number of milliseconds from the beginning of the day (i.e the delta)
   * */
  updateDateTimeState = (props: Props, oldProps?: Props): void => {
    const { value, step, onChange } = props;

    const changes: Record<string, unknown> = {}; // for state changes
    const propChanges: Record<string, unknown> = {}; // in case the values provided aren't exactly ideal

    const startChanged =
      value.start && (oldProps ? value.start !== oldProps.value.start : true);
    const endChanged =
      value.end && (oldProps ? value.end !== oldProps.value.end : true);

    if (startChanged) {
      const newStartDate = moment(value.start).startOf('day');
      const timeDelta = moment(value.start).diff(newStartDate);
      const closestStepTimeDelta = this.getClosestStepTime(timeDelta, step);

      // if the start time provided doesn't fall exactly on a step increment
      // (e.g if user provided time of 5:43pm but the step only allows increments of 15min)
      // then calculate a new proposed start date + time to trigger an onChange() with.
      // (e.g 5:45pm)
      if (timeDelta !== closestStepTimeDelta) {
        propChanges.start = this.getAsMomentDate(
          newStartDate,
          closestStepTimeDelta
        );
      }

      changes.start = {
        date: newStartDate,
        time: timeDelta,
        momentDate: value.start,
      };
    }

    if (endChanged) {
      const end = value.end;

      // if the time slot duration is < the minimum allowed, move the end date
      // by triggering an onChange
      if (value.start && moment(value.start).add(step, 'ms').isAfter(end)) {
        onChange({
          start: value.start,
          end: moment(value.start).add(step, 'ms'),
        });
      }

      const newEndDate = moment(value.end).startOf('day');
      const timeDelta = moment(value.end).diff(newEndDate);
      const closestStepTimeDelta = this.getClosestStepTime(timeDelta, step);

      // if the end time provided doesn't fall exactly on a step increment
      // (e.g if user provided time of 5:43pm but the step only allows increments of 15min)
      // then calculate a new proposed start date + time to trigger an onChange() with.
      if (timeDelta !== closestStepTimeDelta) {
        propChanges.end = this.getAsMomentDate(
          newEndDate,
          closestStepTimeDelta
        );
      }

      changes.end = {
        date: newEndDate,
        time: timeDelta,
        momentDate: value.end,
      };
    }

    // if the start/end date + times have to be revised, then trigger an onChange now
    // and wait for it to trickle back down to this component.
    if (Object.keys(propChanges).length > 0) {
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.warn(
          `start and/or end times provided did not fall on a step increment of
          '${step}ms'. Triggering an onChange with the closest proposed times'`
        );
      }
      // eslint-disable-next-line max-len
      // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type 'TimeSlotValue'.
      onChange(propChanges);
    }

    // avoid trying to set state if there were no changes
    if (Object.keys(changes).length === 0) {
      return;
    }

    // eslint-disable-next-line max-len
    // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
    this.setState(changes);
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      this.props.value !== nextProps.value ||
      this.props.step !== nextProps.step
    ) {
      this.updateDateTimeState(nextProps, this.props);
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (this.props.disabled) {
      return false;
    }

    if (this.state !== nextState) {
      return true;
    }

    const {
      min: oldMin,
      max: oldMax,
      step: oldStep,
      value: oldValue,
    } = this.props;

    const { min, max, step, value } = nextProps;

    if (
      min !== oldMin ||
      max !== oldMax ||
      step !== oldStep ||
      value.start !== oldValue.start ||
      value.end !== oldValue.end
    ) {
      return true;
    }

    return false;
  }

  getAsMomentDate = (date: Moment, timeDelta: number): Moment => {
    return moment(date).add(timeDelta, 'ms');
  };

  /**
   * Returns the next closest matching time working off step increments. The returned value
   * is also a time delta.
   *
   * @param timeDelta - the number of ms since the beginning of the day
   * @param step - the number of ms duration between start/end times
   *
   * For example, if the timeDelta = 18720000 (5:12am) and the step was 900000 (15min),
   * then the time delta returned will be 18900000 (5:15am)
   * */
  getClosestStepTime(timeDelta: number, step: number): number {
    const dayStart = moment().startOf('day');
    const time = moment(dayStart).add(timeDelta, 'ms');

    let stepTime = moment(dayStart);

    while (moment(stepTime).isBefore(time)) {
      stepTime = moment(stepTime).add(step, 'ms');
    }

    return stepTime.diff(dayStart);
  }

  onStartDateChange = (evt: React.SyntheticEvent<HTMLSelectElement>): void => {
    const { value: oldValue, onChange } = this.props;

    const start = oldValue.start;
    const end = oldValue.end;

    // if the start date is changed, change the end date as well.
    // that's because currently, the control is shown like this:
    //
    // | start day | start time | end date time |
    //
    // So even if end date time is on a different date, the user doesn't know that.
    // His/her assumption is that if the only date field was changed, it would affect
    // both fields
    const newVal = evt.currentTarget.value;
    const newStart = moment(newVal).add(
      getTimeElapsedFromStartOfDay(start),
      'ms'
    );
    let newEnd = moment(newVal).add(getTimeElapsedFromStartOfDay(end), 'ms');

    if (newStart.isAfter(end)) {
      newEnd = moment(newStart)
        .startOf('day')
        .add(getTimeElapsedFromStartOfDay(end), 'ms');
    }

    onChange({
      start: newStart,
      end: newEnd,
    });
  };

  onStartTimeChange = (evt: React.SyntheticEvent<HTMLSelectElement>): void => {
    const { start, end } = this.state;
    const { step, onChange } = this.props;
    const newTimeMs = parseInt(evt.currentTarget.value, 10);

    // combine the date and time to a moment date to do comparisons for passing back to parent
    // component
    const newStartDate = this.getAsMomentDate(start.date, newTimeMs);
    let endDate = end.momentDate;

    // if the time selected for the start time makes the time slot smaller than the minimum
    // allowed, then change the end time
    if (this.getAsMomentDate(newStartDate, step).isAfter(endDate)) {
      endDate = this.getAsMomentDate(newStartDate, step);
    }

    onChange({
      start: newStartDate,
      end: endDate,
    });
  };

  onEndTimeChange = (evt: React.SyntheticEvent<HTMLSelectElement>): void => {
    const { start, end } = this.state;
    const { step, onChange } = this.props;
    const newTimeMs = parseInt(evt.currentTarget.value, 10);

    let startDate = start.momentDate;
    const newEndDate = this.getAsMomentDate(end.date, newTimeMs);

    // if the time selected for the end time makes the time slot smaller than the minimum
    // allowed, then change the end time to meet minimum duration
    if (moment(newEndDate).subtract(step, 'ms').isBefore(startDate)) {
      startDate = moment(newEndDate).subtract(step, 'ms');
    }

    onChange({
      start: startDate,
      end: newEndDate,
    });
  };

  formatTime(momentDate: Moment): string {
    const startOfDay = moment(momentDate).startOf('day');
    const timeDelta = moment(momentDate).diff(startOfDay, 'ms');

    cachedLocalizedStrings[timeDelta] =
      cachedLocalizedStrings[timeDelta] || formatToLocalizedTime(momentDate);
    return cachedLocalizedStrings[timeDelta];
  }

  renderDateOptions = (min: Moment, max: Moment, date: Moment) => {
    const dayCount = max.diff(min, 'days');
    const minDate = moment.min(min, date);

    const optionElems: Array<
      React.ReactElement<React.ComponentProps<'option'>>
    > = [];

    for (let i = 0; i < dayCount; i++) {
      const optionDate = moment(minDate).add(i, 'days').startOf('day');

      optionElems.push(
        <option key={i} value={optionDate.toISOString()}>
          {formatToLocalizedDate(optionDate)}
        </option>
      );
    }

    return optionElems;
  };

  renderTimeOptions = (date: Moment, step: number) => {
    const now = moment();
    const startOfDay = moment(date).startOf('day');
    const endOfDay = moment(date).endOf('day');

    // determine the min start time possible
    // if the date selected is today, then we ensure that it's after the current date time
    let minStart = moment();

    // otherwise, the min start time starts from 12am
    if (date && !areDatesOnSameDay(date, now)) {
      minStart = moment(startOfDay);
    }

    const optionElems: Array<
      React.ReactElement<React.ComponentProps<'option'>>
    > = [];
    let stepTime = startOfDay;

    while (stepTime.isBefore(endOfDay)) {
      const val = stepTime.toISOString();

      // save the value as the delta from the beginning of the day
      // so it can be easily combined with the date field.
      const startOfDayDelta = stepTime.diff(startOfDay);
      const disabled = stepTime.isBefore(minStart);

      optionElems.push(
        <option key={val} value={startOfDayDelta} disabled={disabled}>
          {this.formatTime(stepTime)}
        </option>
      );

      stepTime = moment(stepTime).add(step, 'ms');
    }

    return optionElems;
  };

  render() {
    /* eslint-disable no-unused-vars */
    const { disabled, min, max, step, onChange, ...rest } = this.props;
    /* eslint-enable no-unused-vars */

    const { start, end } = this.state;

    // show a basic view
    if (disabled && start.date && end.date) {
      const startDayStr = formatToLocalizedDay(start.date);
      const startTimeStr = formatToLocalizedTime(start.momentDate);
      const endTimeStr = formatToLocalizedTime(end.momentDate);

      return (
        <ReadOnlyDateTime {...rest}>
          {/* eslint-disable-next-line react/jsx-no-literals */}
          <span>{startDayStr}:</span>
          <span>
            {/* eslint-disable-next-line react/jsx-no-literals */}
            {startTimeStr} - {endTimeStr}
          </span>
        </ReadOnlyDateTime>
      );
    }

    if (start && end) {
      return (
        <FlexContainer data-provides={'time-slot-input'} {...rest}>
          <WrappedDropdown
            data-provides={'time-slot-input-start-date'}
            onChange={this.onStartDateChange}
            value={start.date.toISOString()}
            date
          >
            {this.renderDateOptions(min, max, start.momentDate)}
          </WrappedDropdown>
          <WrappedDropdown
            data-provides={'time-slot-input-start-time'}
            onChange={this.onStartTimeChange}
            value={start.time}
            time
          >
            {this.renderTimeOptions(start.momentDate, step)}
          </WrappedDropdown>
          <WrappedDropdown
            data-provides={'time-slot-input-end-time'}
            onChange={this.onEndTimeChange}
            value={end.time}
            time
          >
            {this.renderTimeOptions(end.momentDate, step)}
          </WrappedDropdown>
        </FlexContainer>
      );
    }

    return null;
  }
}
